import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SendIcon from '@mui/icons-material/Send'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import SaveIcon from '@mui/icons-material/Save'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import API from '../../services/API'
import { DataGrid } from '@mui/x-data-grid'
import { CRMContext } from '../CRM'
import { audienceLabel, STATUS_CHIP_COLORS } from './Campaigns'

const StatTile = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      flex: 1,
      border: '1px solid #E2E8F0',
      borderRadius: 3,
      textAlign: 'center',
    }}
  >
    <Typography variant="h4" fontWeight={900}>
      {value ?? '—'}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Paper>
)

export default function CampaignDetail() {
  const { campaignID } = useParams()
  const navigate = useNavigate()
  const { user } = useContext(CRMContext)

  const [campaign, setCampaign] = useState(null)
  const [recipients, setRecipients] = useState([])
  const [loading, setLoading] = useState(true)

  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState('')

  const [testEmail, setTestEmail] = useState(user?.email ?? '')
  const [testStatus, setTestStatus] = useState(null)

  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [sendConfirmation, setSendConfirmation] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')

  const [contentForm, setContentForm] = useState(null)
  const [contentSaving, setContentSaving] = useState(false)
  const [contentStatus, setContentStatus] = useState(null)

  const contentFormFromCampaign = (data) => ({
    subject: data.subject ?? data.name ?? '',
    intro: data.content?.intro ?? '',
    features: (data.content?.features?.length
      ? data.content.features
      : [{ title: '', description: '' }]
    ).map((feature) => ({
      title: feature.title ?? '',
      description: feature.description ?? '',
    })),
    ctaLabel: data.content?.ctaLabel ?? '',
    ctaUrl: data.content?.ctaUrl ?? '',
  })

  const loadCampaign = async () => {
    const res = await API.get(`/admincrm/email-campaigns/${campaignID}`)
    if (res.status === 200) {
      setCampaign(res.data.campaign)
      setRecipients(res.data.recipients ?? [])
      if (res.data.campaign?.type === 'builtIn' && res.data.campaign?.status === 'draft') {
        setContentForm(contentFormFromCampaign(res.data.campaign))
      }
    }
    setLoading(false)
  }

  const loadPreview = async () => {
    setPreviewError('')
    const res = await API.get(`/admincrm/email-campaigns/${campaignID}/preview`)
    if (res.status === 200) {
      setPreview(res.data)
    } else {
      setPreviewError(res.data?.message ?? 'Failed to load the preview from Postmark')
    }
  }

  useEffect(() => {
    loadCampaign()
    loadPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignID])

  const sendTest = async () => {
    setTestStatus({ severity: 'info', message: 'Sending test email…' })
    const res = await API.post(`/admincrm/email-campaigns/${campaignID}/test-send`, {
      email: testEmail,
    })
    if (res.status === 200) {
      setTestStatus({ severity: 'success', message: `Test email sent to ${testEmail}` })
    } else {
      setTestStatus({
        severity: 'error',
        message: res.data?.message ?? 'Failed to send the test email',
      })
    }
  }

  const saveContent = async ({ silent = false } = {}) => {
    if (!contentForm) return true
    setContentSaving(true)
    if (!silent) setContentStatus(null)
    const res = await API.put(`/admincrm/email-campaigns/${campaignID}`, {
      subject: contentForm.subject,
      content: {
        intro: contentForm.intro,
        features: contentForm.features,
        ctaLabel: contentForm.ctaLabel,
        ctaUrl: contentForm.ctaUrl,
      },
    })
    setContentSaving(false)
    if (res.status !== 200) {
      setContentStatus({
        severity: 'error',
        message: res.data?.message ?? 'Failed to save the campaign content',
      })
      return false
    }
    setCampaign((prev) => ({ ...prev, ...res.data }))
    if (!silent) setContentStatus({ severity: 'success', message: 'Saved — preview updated' })
    loadPreview()
    return true
  }

  const updateFeature = (index, field, value) =>
    setContentForm((prev) => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === index ? { ...feature, [field]: value } : feature,
      ),
    }))

  const moveFeature = (index, direction) =>
    setContentForm((prev) => {
      const features = [...prev.features]
      const target = index + direction
      if (target < 0 || target >= features.length) return prev
      ;[features[index], features[target]] = [features[target], features[index]]
      return { ...prev, features }
    })

  const removeFeature = (index) =>
    setContentForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }))

  const addFeature = () =>
    setContentForm((prev) => ({
      ...prev,
      features: [...prev.features, { title: '', description: '' }],
    }))

  const sendCampaign = async () => {
    setSending(true)
    setSendError('')
    // Make sure the latest edits are what actually gets sent
    if (campaign.type === 'builtIn' && contentForm) {
      const saved = await saveContent({ silent: true })
      if (!saved) {
        setSending(false)
        setSendError('Could not save your latest edits — the campaign was not sent')
        return
      }
    }
    const res = await API.post(`/admincrm/email-campaigns/${campaignID}/send`)
    setSending(false)
    if (res.status !== 200) {
      setSendError(res.data?.message ?? 'Failed to send the campaign')
      return
    }
    setSendDialogOpen(false)
    setSendConfirmation('')
    setCampaign(res.data)
    setRecipients([])
  }

  const recipientColumns = [
    {
      field: 'contactName',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Typography fontWeight={700}>{row.contactName || '—'}</Typography>
      ),
    },
    { field: 'contactEmail', headerName: 'Email', flex: 1.2, minWidth: 220 },
    { field: 'organisationName', headerName: 'Organisation', flex: 1, minWidth: 180 },
    { field: 'stage', headerName: 'Stage', minWidth: 130 },
  ]

  const failureColumns = [
    { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 220 },
    { field: 'errorCode', headerName: 'Error code', minWidth: 110 },
    { field: 'message', headerName: 'Message', flex: 1.6, minWidth: 260 },
  ]

  if (loading) {
    return (
      <Stack alignItems="center" py={10}>
        <CircularProgress />
      </Stack>
    )
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Campaign not found.</Alert>
      </Box>
    )
  }

  const isDraft = campaign.status === 'draft'
  const isBuiltIn = campaign.type === 'builtIn'
  const recipientNoun =
    campaign.audience === 'siteAdmins' ? 'site admins' : 'recipients'

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: 'hidden',
          border: '1px solid #E2E8F0',
          boxShadow: '0 18px 60px rgba(36,47,64,0.12)',
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 52%, #239AA4 150%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/campaigns')}
                sx={{ color: 'rgba(255,255,255,0.78)', textTransform: 'none', mb: 1 }}
              >
                All campaigns
              </Button>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="h4" fontWeight={900} color="white">
                  {campaign.name}
                </Typography>
                <Chip
                  label={campaign.status}
                  color={STATUS_CHIP_COLORS[campaign.status] ?? 'default'}
                  sx={{ textTransform: 'capitalize', fontWeight: 700, color: 'white' }}
                />
              </Stack>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.78)' }}
              >
                Template:{' '}
                {isBuiltIn
                  ? `${campaign.templateName ?? "What's New"} (built-in)`
                  : `${campaign.template?.name ?? campaign.templateName} (#${campaign.postmarkTemplateId})`}{' '}
                &middot; {audienceLabel(campaign.audience)}
                {campaign.sentAt &&
                  ` · Sent ${moment(campaign.sentAt).format('DD/MM/YYYY HH:mm')}${
                    campaign.sentBy ? ` by ${campaign.sentBy}` : ''
                  }`}
              </Typography>
            </Box>
            {isDraft && (
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => setSendDialogOpen(true)}
                disabled={!recipients.length}
                sx={{
                  alignSelf: { xs: 'flex-start', md: 'center' },
                  textTransform: 'none',
                  fontWeight: 900,
                  bgcolor: '#239AA4',
                  '&:hover': { bgcolor: '#1D858E' },
                }}
              >
                Send to {campaign.recipientCount ?? recipients.length} {recipientNoun}
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <StatTile label="Recipients" value={campaign.recipientCount} />
              <StatTile
                label="Delivered to Postmark"
                value={isDraft ? '—' : campaign.sentCount}
              />
              <StatTile
                label="Failed"
                value={isDraft ? '—' : campaign.failedCount}
              />
            </Stack>

            {campaign.status === 'sent' && (
              <Alert severity="success">
                This campaign has been sent — {campaign.sentCount} of{' '}
                {campaign.recipientCount} emails were accepted by Postmark.
              </Alert>
            )}
            {campaign.status === 'failed' && (
              <Alert severity="error">
                This campaign failed to send. Check the failures below and the
                server logs, then create a new campaign to retry.
              </Alert>
            )}

            {!isDraft && campaign.failures?.length > 0 && (
              <Box>
                <Typography variant="h6" fontWeight={900} mb={1}>
                  Failures
                </Typography>
                <DataGrid
                  rows={campaign.failures}
                  columns={failureColumns}
                  getRowId={(row) => row._id ?? row.email}
                  autoHeight
                  disableRowSelectionOnClick
                  sx={{ border: '1px solid #E2E8F0', borderRadius: 3 }}
                />
              </Box>
            )}

            {isBuiltIn && isDraft && contentForm && (
              <Paper
                elevation={0}
                sx={{ p: { xs: 2, md: 3 }, border: '1px solid #E2E8F0', borderRadius: 3 }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={900}>
                      What&apos;s New content
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      List a title (and optional description) for each new
                      feature — the branded email is generated for you.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={contentSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                    onClick={() => saveContent()}
                    disabled={contentSaving}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 900,
                      bgcolor: '#239AA4',
                      '&:hover': { bgcolor: '#1D858E' },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {contentSaving ? 'Saving…' : 'Save & refresh preview'}
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  <TextField
                    label="Email subject / headline"
                    value={contentForm.subject}
                    onChange={(e) =>
                      setContentForm((prev) => ({ ...prev, subject: e.target.value }))
                    }
                    placeholder="e.g. What's new in AmbuNet — August 2026"
                    fullWidth
                  />
                  <TextField
                    label="Intro paragraph (optional)"
                    value={contentForm.intro}
                    onChange={(e) =>
                      setContentForm((prev) => ({ ...prev, intro: e.target.value }))
                    }
                    placeholder="A friendly default intro is used if you leave this blank"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <Typography variant="subtitle2" fontWeight={800}>
                    Features
                  </Typography>
                  {contentForm.features.map((feature, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{
                        p: 1.5,
                        border: '1px solid #EEF2F7',
                        borderRadius: 2,
                        bgcolor: '#F8FAFC',
                      }}
                    >
                      <Stack spacing={1} flex={1}>
                        <TextField
                          label={`Feature ${index + 1} title`}
                          value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Description (optional)"
                          value={feature.description}
                          onChange={(e) =>
                            updateFeature(index, 'description', e.target.value)
                          }
                          size="small"
                          fullWidth
                          multiline
                        />
                      </Stack>
                      <Stack>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() => moveFeature(index, -1)}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === contentForm.features.length - 1}
                              onClick={() => moveFeature(index, 1)}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove feature">
                          <IconButton size="small" onClick={() => removeFeature(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addFeature}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 800 }}
                  >
                    Add feature
                  </Button>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Button label (optional)"
                      value={contentForm.ctaLabel}
                      onChange={(e) =>
                        setContentForm((prev) => ({ ...prev, ctaLabel: e.target.value }))
                      }
                      placeholder="Log in to AmbuNet"
                      fullWidth
                    />
                    <TextField
                      label="Button link (optional)"
                      value={contentForm.ctaUrl}
                      onChange={(e) =>
                        setContentForm((prev) => ({ ...prev, ctaUrl: e.target.value }))
                      }
                      placeholder="https://ambunet.co.uk"
                      fullWidth
                    />
                  </Stack>
                  {contentStatus && (
                    <Alert severity={contentStatus.severity}>{contentStatus.message}</Alert>
                  )}
                </Stack>
              </Paper>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Typography variant="h6" fontWeight={900} mb={1}>
                  {!isDraft && preview?.isSentCopy ? 'Copy of the sent email' : 'Preview'}
                </Typography>
                {previewError ? (
                  <Alert severity="error">{previewError}</Alert>
                ) : !preview ? (
                  <Stack alignItems="center" py={6}>
                    <CircularProgress />
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Typography>
                      <strong>Subject:</strong> {preview.subject}
                    </Typography>
                    <Box
                      component="iframe"
                      title="Email preview"
                      srcDoc={preview.htmlBody ?? preview.textBody ?? ''}
                      sandbox=""
                      sx={{
                        width: '100%',
                        height: '65vh',
                        border: '1px solid #E2E8F0',
                        borderRadius: 2,
                        bgcolor: 'white',
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {preview.isSentCopy
                        ? 'This is the exact copy stored when the campaign was sent.'
                        : `Rendered with ${
                            recipients.length
                              ? `the first recipient's details (${recipients[0].contactEmail})`
                              : 'sample data'
                          }. Each recipient receives their own personalised greeting.`}
                    </Typography>
                  </Stack>
                )}
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" fontWeight={900} mb={1}>
                      Send a test
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="Test email address"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        startIcon={<ForwardToInboxIcon />}
                        onClick={sendTest}
                        disabled={!testEmail}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Send test
                      </Button>
                    </Stack>
                    {testStatus && (
                      <Alert severity={testStatus.severity} sx={{ mt: 1 }}>
                        {testStatus.message}
                      </Alert>
                    )}
                  </Box>

                  {isDraft && (
                    <Box>
                      <Typography variant="h6" fontWeight={900} mb={1}>
                        Recipients ({recipients.length})
                      </Typography>
                      <DataGrid
                        rows={recipients}
                        columns={recipientColumns}
                        getRowId={(row) => row._id}
                        autoHeight
                        density="compact"
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                          pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        sx={{ border: '1px solid #E2E8F0', borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {campaign.audience === 'siteAdmins'
                          ? 'Site admins are gathered live from every active tenant. Archived users and duplicate email addresses are excluded automatically, and the list is re-checked at the moment you send.'
                          : 'Archived leads and duplicate email addresses are excluded automatically. The list is re-checked at the moment you send.'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </Paper>

      <Dialog
        open={sendDialogOpen}
        onClose={() => !sending && setSendDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send this campaign?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This will email{' '}
              <strong>
                {campaign.recipientCount ?? recipients.length} {recipientNoun}
              </strong>{' '}
              via Postmark ({audienceLabel(campaign.audience)}) using{' '}
              {isBuiltIn
                ? 'the built-in "What\'s New" email'
                : `the template "${campaign.template?.name ?? campaign.templateName}"`}
              . This cannot be undone.
            </Alert>
            <TextField
              label='Type "SEND" to confirm'
              value={sendConfirmation}
              onChange={(e) => setSendConfirmation(e.target.value)}
              fullWidth
              autoFocus
            />
            {sendError && <Alert severity="error">{sendError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={sending ? <CircularProgress size={18} /> : <SendIcon />}
            disabled={sendConfirmation !== 'SEND' || sending}
            onClick={sendCampaign}
          >
            {sending ? 'Sending…' : 'Send campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
